import { Injectable } from '@angular/core';
import { GeoJsonPostsResponse, PostsService } from '@mzima-client/sdk';
import * as L from 'leaflet';
import { Observable, tap } from 'rxjs';
import { MAP_CONSTANTS } from '../constants/map';

export interface TimelineCacheConfig {
  minYear: number;
  maxCachedYears: number;
  singlePinMinZoom: number;
}

export interface TimelineYearData {
  monthsData: Map<string, any[]>; // Key: "YYYY-MM"
  bounds?: L.LatLngBounds;
}

@Injectable()
export class MapTimelineDataService {
  private monthCache: Map<string, any[]> = new Map<string, any[]>(); // Key: "YYYY-MM"
  private yearAccessOrder: number[] = [];
  private config: TimelineCacheConfig = {
    minYear: 2025,
    maxCachedYears: 3,
    singlePinMinZoom: 18,
  };

  constructor(private postsService: PostsService) {}

  /**
   * Configure the timeline data service
   */
  public configure(config: Partial<TimelineCacheConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Fetch all posts for a specific year
   */
  public fetchYearData(year: number, baseParams: any): Observable<GeoJsonPostsResponse> {
    const yearStart = new Date(year, 0, 1).toISOString();
    const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999).toISOString();

    const { post_date_after, post_date_before, date_after, date_before, ...cleanParams } =
      baseParams;

    const yearParams = {
      ...cleanParams,
      date: {
        start: yearStart,
        end: yearEnd,
      },
      page: 1,
      limit: MAP_CONSTANTS.PAGINATION.TIMELINE_LIMIT,
    };

    return this.postsService.getGeojson(yearParams).pipe(
      tap((yearData) => {
        this.cacheYearData(year, yearData);
      }),
    );
  }

  /**
   * Cache year data by splitting it into months
   */
  private cacheYearData(year: number, yearData: GeoJsonPostsResponse): void {
    this.evictLRUYearIfNeeded();
    this.splitYearDataByMonth(year, yearData);
    this.touchYear(year);
  }

  /**
   * Split year data into months and cache them
   */
  private splitYearDataByMonth(year: number, yearData: GeoJsonPostsResponse): void {
    for (let month = 0; month < 12; month++) {
      const monthKey = this.getMonthKey(year, month);
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const monthPosts = yearData.results.filter((post) => {
        if (!post.post_date) {
          return false;
        }
        const postDate = new Date(post.post_date);
        return postDate >= monthStart && postDate <= monthEnd;
      });

      this.monthCache.set(monthKey, monthPosts);
    }
  }

  /**
   * Get posts for a specific month range
   */
  public getMonthRangeData(year: number, startMonth: number, endMonth: number): any[] {
    const combinedPosts: any[] = [];

    for (let m = startMonth; m <= endMonth; m++) {
      const key = this.getMonthKey(year, m);
      const monthPosts = this.monthCache.get(key) ?? [];
      combinedPosts.push(...monthPosts);
    }

    return combinedPosts;
  }

  /**
   * Check if a year is fully-cached
   */
  public isYearCached(year: number): boolean {
    const yearKeys = this.getYearMonthKeys(year);
    return yearKeys.every((key) => this.monthCache.has(key));
  }

  /**
   * Check if specific months are cached
   */
  public areMonthsCached(year: number, startMonth: number, endMonth: number): boolean {
    for (let m = startMonth; m <= endMonth; m++) {
      if (!this.monthCache.has(this.getMonthKey(year, m))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Mark a year as recently accessed for LRU tracking
   */
  public touchYear(year: number): void {
    const index = this.yearAccessOrder.indexOf(year);
    if (index > -1) {
      this.yearAccessOrder.splice(index, 1);
    }
    this.yearAccessOrder.push(year);
  }

  /**
   * Evict the least recently used (LRU) year if cache is full
   */
  private evictLRUYearIfNeeded(): void {
    if (this.yearAccessOrder.length >= this.config.maxCachedYears) {
      const yearToEvict = this.yearAccessOrder.shift()!;

      // Remove all month entries for the evicted year
      const keysToRemove = this.getYearMonthKeys(yearToEvict);
      keysToRemove.forEach((key) => this.monthCache.delete(key));
    }
  }

  /**
   * Get month key in format "YYYY-MM"
   */
  private getMonthKey(year: number, month: number): string {
    return `${year}-${month}`;
  }

  /**
   * Get all month keys for a given year
   */
  private getYearMonthKeys(year: number): string[] {
    const keys: string[] = [];
    for (let month = 0; month < 12; month++) {
      keys.push(this.getMonthKey(year, month));
    }
    return keys;
  }

  /**
   * Clear all cached data
   */
  public clearCache(): void {
    this.monthCache.clear();
    this.yearAccessOrder = [];
  }
}
